import { createReducer } from "typesafe-actions";

import { FieldAgentRecordBrief } from "@mapmycustomers/shared/types/chat/FieldAgentChat";
import { FiCatalogResponse } from "@mapmycustomers/shared/types/entity/fieldInsight";

import { Actions, fetchRecordBrief, fetchRecordChartCatalog } from "./actions";

export interface RecordBriefState {
  brief: FieldAgentRecordBrief | undefined;
  charts: FiCatalogResponse[];
  // Distinct from an empty `charts` array, which is also what a genuinely chart-less record type
  // looks like: this is what lets the settings popover tell "nothing to show" apart from "haven't
  // asked yet" / "the ask failed", and skip refetching a catalog it already has.
  chartsFailed: boolean;
  chartsLoaded: boolean;
  chartsLoading: boolean;
  // Set where the failure is known rather than derived from the error's truthiness, which would
  // read a thrown empty string as "no brief yet" and leave the card silent.
  failed: boolean;
  loading: boolean;
}

export const initialState: RecordBriefState = {
  brief: undefined,
  charts: [],
  chartsFailed: false,
  chartsLoaded: false,
  chartsLoading: false,
  failed: false,
  loading: false,
};

const recordBrief = createReducer<RecordBriefState, Actions>(initialState)
  // One slot for one card: the Company pane is the only surface rendering it, and the pane swaps
  // its record in place, so clearing on request is what stops the previous record's summary
  // showing under the next one's name. A second pane rendering the card shares this slot and each
  // request would blank the other's summary — that is the point to key it by record, the way
  // getChatThreadKey keys threads.
  .handleAction(fetchRecordBrief.request, (state) => ({
    ...state,
    brief: undefined,
    failed: false,
    loading: true,
  }))
  .handleAction(fetchRecordBrief.success, (state, { payload }) => ({
    ...state,
    brief: payload,
    loading: false,
  }))
  .handleAction(fetchRecordBrief.failure, (state) => ({
    ...state,
    failed: true,
    loading: false,
  }))
  .handleAction(fetchRecordChartCatalog.request, (state) => ({
    ...state,
    chartsFailed: false,
    chartsLoading: true,
  }))
  .handleAction(fetchRecordChartCatalog.success, (state, { payload }) => ({
    ...state,
    charts: payload,
    chartsLoaded: true,
    chartsLoading: false,
  }))
  .handleAction(fetchRecordChartCatalog.failure, (state) => ({
    ...state,
    chartsFailed: true,
    chartsLoading: false,
  }));

export type RecordBriefActions = Actions;

export default recordBrief;
